import React, { FunctionComponent, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, styled } from '@mui/styles';
import {
  Autocomplete,
  Button,
  Grid,
  Link,
  TextField,
  Box,
  Card,
  CardContent,
  InputAdornment,
  Popper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { SystemAccessoriesContext } from '../../adminSettings/contexts/SystemAccessoriesContext';
import { EstimateCustomersContext } from '../../estimateCustomers/contexts/EstimateCustomersContext';
import { EstimateDetailsContext } from '../../estimateDetails/contexts/EstimateDetailsContext';
import TextInput from '../../form/components/TextInput';
import FilterSelectInput from '../../form/components/FilterSelectInput';
import SelectInput from '../../form/components/SelectInput';
import { EquipmentCostFormValues } from '../model';
import DataTable from '../../dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../dataTable/DataTable/model';
import InformationField from '../../form/components/InformationField';
import { EstimateCostsContext } from '../contexts/EstimateCostsContext';
import axios from '../../utils/axios.utils';
import ExpandedSpecifxDetails from './ExpandedSpecifxDetails';
import ReplacementEquipmentFilterBar from './ReplacementEquipmentFilterBar';

// Does this form need to be updated to allow for adding multiple equipment costs from the single submit button?

// Set the default value for the cost field when a piece of equipment is chosen from the AutoComplete

const CustomPopper = styled(Popper)(({ theme }) => ({}));

const filterChoices = {
  manufacturer_choices: [
    { label: 'Carrier', value: 'carrier' },
    { label: 'Trane', value: '2' },
  ],
  tons_choices: [
    { label: '3-5', value: 'small' },
    { label: '6-12.5', value: 'medium' },
    { label: '15-25', value: 'large' },
    { label: '25+', value: 'xlarge' },
  ],
  heating_choices: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
  orientation_choices: [
    { label: 'Horizontal', value: 'horizontal' },
    { label: 'Vertical', value: 'vertical' },
    { label: 'Combo', value: 'combo' },
  ],
};

const EquipmentCostForm = (props) => {
  const history = useHistory();
  const { salesEstimateId } = props;

  const { systemAccessories, systemAccessoriesLoading } = React.useContext(SystemAccessoriesContext);
  const { salesEstimate } = React.useContext(EstimateDetailsContext);
  const { equipmentCosts } = React.useContext(EstimateCostsContext);

  const [assetDetailsModalOpen, setAssetDetailsModalOpen] = useState(false);
  const [modalDetails, setModalDetails] = useState([]);

  const handleOpen = (details) => {
    setModalDetails(details);
    setAssetDetailsModalOpen(true);
  };

  const handleClose = () => {
    setAssetDetailsModalOpen(false);
    setModalDetails([]);
  };

  const { control, handleSubmit, register } = useForm({
    defaultValues: {
      name: '',
      systemAccessories: [],
    },
  });

  const {
    fields: accessoryFields,
    append: appendAccessoryCost,
    remove: removeAccessoryCost,
    update: updateSystemAccessory,
  } = useFieldArray({
    control,
    name: 'systemAccessories',
  });

  const [formValues, setFormValues] = React.useState(
    salesEstimate.sales_estimate_assets?.map((asset) => ({
      manufacturer_id: asset.manufacturer_id || 'Carrier',
      equipment_cost_id: asset.equipment_cost_id || 'Select Replacement',
      unit_configuration: asset.unit_configuration || 'horizontal',
      equipment_cost_amount: asset.equipment_cost_amount,
    })),
  );

  React.useEffect(() => {
    if (accessoryFields.length === 0 && salesEstimate.asset_accessories.length > 0) {
      salesEstimate.asset_accessories.forEach((asset_accessory, index) => {
        // if(index === 0) {
        appendAccessoryCost({
          asset_id: asset_accessory.asset_id,
          item_name: asset_accessory.accessory_id,
          cost: asset_accessory.accessory_cost,
        });
        // }
      });
    }
  }, [salesEstimate]);

  const handleValueChange = (index, field, value) => {
    const updatedFormValues = [...formValues];
    updatedFormValues[index] = { ...updatedFormValues[index], [field]: value };
    setFormValues(updatedFormValues);
  };

  const columns: IDataTableColumn<any>[] = [
    {
      name: 'Asset Number',
      selector: 'asset_number',
      sortable: true,
      cell: (row) => <p>{row.asset_number} (Full)</p>,
    },
    {
      name: 'Size',
      selector: 'size',
      grow: 0.7,
      sortable: true,
    },
    {
      name: 'Replacement Unit',
      conditionalCellStyles: [{ when: (row) => true, style: { width: '100%', '& .MuiBox-root': { width: '100%' } } }],
      selector: 'replacement_equipment',
      grow: 4.5,
      sortable: true,
      cell: (row) => (
        <Box className="full-width-select" sx={{ position: 'relative', zIndex: 1 }}>
          <Autocomplete
            disablePortal
            PopperComponent={CustomPopper}
            id="combo-box-demo"
            options={[{ label: 'Select Replacement', value: 'Select Replacement' }, ...equipmentCosts]}
            getOptionLabel={(option: any) => option.label}
            renderOption={(props, option: any) => (
              <Box component="li" {...props} display="flex" alignItems="center">
                {option.label}
                {option.details && (
                  <IconButton
                    edge="end"
                    aria-label="details"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen(option.details);
                    }}
                  >
                    <InfoIcon />
                  </IconButton>
                )}
              </Box>
            )}
            sx={{ width: '95%', float: 'left' }}
            value={
              equipmentCosts.filter(
                (costChoice) => costChoice.value === formValues[row?.index]?.equipment_cost_id,
              )[0] || { label: 'Select Replacement', value: 'Select Replacement' }
            }
            onChange={(event, newValue: any) => handleValueChange(0, 'equipment_cost_id', newValue?.value)}
            renderInput={(params) => <TextField {...params} />}
          />
          <IconButton
            edge="end"
            aria-label="details"
            style={{ float: 'right', marginTop: 5 }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpen(
                (
                  equipmentCosts.filter(
                    (costChoice) => costChoice.value === formValues[row?.index]?.equipment_cost_id,
                  )[0] as any
                )?.details,
              );
            }}
          >
            <InfoIcon />
          </IconButton>
        </Box>
      ),
    },
    {
      name: 'Equipment Cost',
      selector: 'equipment_cost',
      sortable: true,
      cell: (row) => (
        <TextField
          label=""
          placeholder={
            (
              equipmentCosts.filter(
                (costChoice) => costChoice.value === formValues[row?.index]?.equipment_cost_id,
              )[0] as any
            )?.default_cost
          }
          variant="outlined"
          value={formValues[row?.index]?.equipment_cost_amount}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      ),
    },
  ];

  const onSubmit = (data) => {
    axios
      .post(`/sales_estimates/${salesEstimateId}/equipment_replacements`, {
        equipment_costs: formValues,
        system_accessories: accessoryFields,
      })
      .then((response) => {
        history.push(`/rooftop_automation/${salesEstimateId}/crane_curb`);
      });
  };

  return (
    equipmentCosts && (
      <>
        <ReplacementEquipmentFilterBar filterChoices={filterChoices} />

        {salesEstimate.sales_estimate_assets.map((asset, index) => (
          <Card key={index} style={{ marginBottom: 15 }}>
            <CardContent>
              <Grid container spacing={3} style={{ display: 'flex' }}>
                <Grid item style={{ width: '40%' }}>
                  <Typography style={{ fontSize: '20px' }}>
                    <span style={{ fontWeight: 'bold' }}>{asset.asset_number}</span> (Full)
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography>size: {asset.size}</Typography>
                </Grid>
              </Grid>
              <br />
              <Grid container style={{ display: 'flex', alignItems: 'center' }}>
                <Grid item xs={2}>
                  <Typography>Replace with:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Autocomplete
                    disablePortal
                    PopperComponent={CustomPopper}
                    id="combo-box-demo"
                    options={[{ label: 'Select Replacement', value: 'Select Replacement' }, ...equipmentCosts]}
                    getOptionLabel={(option: any) => option.label}
                    renderOption={(props, option: any) => (
                      <Box component="li" {...props} display="flex" alignItems="center">
                        {option.label}
                        {option.details && (
                          <IconButton
                            edge="end"
                            aria-label="details"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpen(option.details);
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        )}
                      </Box>
                    )}
                    sx={{ width: '95%', float: 'left' }}
                    value={
                      equipmentCosts.filter(
                        (costChoice) => costChoice.value === formValues[index]?.equipment_cost_id,
                      )[0] || { label: 'Select Replacement', value: 'Select Replacement' }
                    }
                    onChange={(event, newValue: any) => handleValueChange(0, 'equipment_cost_id', newValue?.value)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="Equipment Cost"
                    // placeholder={
                    //   (
                    //     equipmentCosts.filter(
                    //       (costChoice) => costChoice.value === formValues[row?.index]?.equipment_cost_id,
                    //     )[0] as any
                    //   )?.default_cost
                    // }
                    variant="outlined"
                    value={formValues[index]?.equipment_cost_amount}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
              <Grid style={{ marginTop: 10 }}>
                <Grid item style={{ paddingLeft: '5%', paddingRight: '5%' }}>
                  <Accordion>
                    <AccordionSummary style={{ backgroundColor: '#dcf5e6' }}>
                      <Typography style={{ fontWeight: 'bold' }}>Specifx Decoded Asset Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <ExpandedSpecifxDetails asset />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
        {/* <Card>
          <CardContent>
            <DataTable
              columns={columns}
              data={salesEstimate.sales_estimate_assets}
              striped
              dense
              highlightOnHover
              pointerOnHover
              expandableRows
              expandableRowsComponent={<ExpandedSpecifxDetails row />}
              noHeader
              conditionalCellStyles={[
                {
                  when: (row) => row.selector == 'replacement_equipment',
                  style: {
                    backgroundColor: 'yellow',
                  },
                  classNames: ['custom-class'],
                },
              ]}
            />
          </CardContent>
        </Card> */}
        <br />
        <hr />
        <br />
        <Dialog open={assetDetailsModalOpen} onClose={handleClose}>
          <DialogTitle>Additional Details</DialogTitle>
          <DialogContent>
            <div className="expanded-row" style={{ width: '100%' }}>
              <Grid container spacing={3}>
                {modalDetails?.map((key_value) => (
                  <Grid item xs={4}>
                    <InformationField label={key_value.label} value={key_value.value || 'Unknown'} />
                  </Grid>
                ))}
              </Grid>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        </Dialog>
        <Grid container justifyContent="space-between">
          <Grid item xs={12}>
            <h2 style={{ fontSize: '18px', marginBottom: 10 }}>System Accessories</h2>
          </Grid>
          <Grid item xs={12}>
            {accessoryFields.map((item, index) => (
              <>
                <Grid container spacing={2}>
                  <Grid item md={3} xs={6}>
                    <Controller
                      name={`systemAccessories.${index}.asset_id`}
                      control={control}
                      render={({ field }) => (
                        <SelectInput
                          {...field}
                          label="Asset"
                          value={salesEstimate.sales_estimate_assets[0]?.id}
                          choices={[
                            { label: 'All Units', value: 'All Units' },
                            ...(salesEstimate.sales_estimate_assets as any)?.map((asset) => ({
                              label: asset.asset_number,
                              value: asset.id,
                            })),
                          ]}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={3} xs={6}>
                    <Controller
                      name={`systemAccessories.${index}.item_name`}
                      control={control}
                      render={({ field }) => (
                        <SelectInput
                          {...field}
                          label="Item Name (Multi-select)"
                          choices={systemAccessories.map((accessory) => ({
                            label: accessory.name,
                            value: accessory.id,
                          }))}
                          onChange={(e: any) => {
                            const selectedAccessory = systemAccessories.find(
                              (accessory) => accessory.name === e.target.value,
                            );

                            if (selectedAccessory) {
                              updateSystemAccessory(index, {
                                ...accessoryFields[index],
                                item_name: e.target.value,
                                cost: selectedAccessory.formatted_cost,
                              });
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={3} xs={6}>
                    <Controller
                      name={`systemAccessories.${index}.cost`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Cost"
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={3} xs={6}>
                    <Button onClick={() => removeAccessoryCost(index)}>Remove</Button>
                  </Grid>
                </Grid>
                <br />
              </>
            ))}
          </Grid>
        </Grid>

        <Button onClick={() => appendAccessoryCost({})} type="button">
          Add System Accessory
        </Button>
        <br />
        <hr />
        <br />
        <Button variant="contained" color="primary" onClick={onSubmit}>
          Submit Equipment Details
        </Button>
      </>
    )
  );
};

export default EquipmentCostForm;
